import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
    syncWhiteboard,
    detachWhiteboard,
    WHITEBOARD_STATUS,
} from '../../../hooks/useWhiteboard';

/**
 * Zoom pasa de Pending a InProgress cuando la pizarra terminó de cargar. Si se
 * queda en Pending suele ser que su backend de pizarra (DAS) rechazó la sesión.
 */
const LOAD_TIMEOUT_MS = 15000;

/**
 * Superficie de la pizarra colaborativa.
 * - Anfitrión: inicia/cierra con startWhiteboardScreen / stopWhiteboardScreen.
 * - Alumnos: entran/salen solos vía peer-whiteboard-state-change (start/view).
 */
export const VideoWhiteboardStage = ({ whiteboard, isHost = false }) => {
    const hostRef = useRef(null);
    const [loadFailed, setLoadFailed] = useState(false);

    const {
        isOpen,
        wantsPresent,
        activePresenterId,
        attachMode,
        supported,
        deviceLimited,
        presenterName,
        status,
    } = whiteboard;

    const [hostFilled, setHostFilled] = useState(false);

    const attached = attachMode != null;
    const loaded = status === WHITEBOARD_STATUS.InProgress || hostFilled;
    const presenting = isHost && (attachMode === 'present' || wantsPresent);

    useEffect(() => {
        if (!isOpen) return;
        void syncWhiteboard(hostRef.current);
    }, [isOpen, wantsPresent, activePresenterId, attachMode]);

    useEffect(() => {
        const host = hostRef.current;
        if (!isOpen || !host) return undefined;
        const sync = () => setHostFilled(host.childElementCount > 0);
        sync();
        const observer = new MutationObserver(sync);
        observer.observe(host, { childList: true });
        return () => observer.disconnect();
    }, [isOpen]);

    useEffect(() => {
        setLoadFailed(false);
        if (!attached || loaded) return undefined;
        const timer = setTimeout(() => setLoadFailed(true), LOAD_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [attached, loaded]);

    useEffect(() => () => {
        void detachWhiteboard();
    }, []);

    if (!isOpen) return null;

    const label = presenting
        ? 'Estás presentando la pizarra'
        : `Pizarra de ${presenterName || 'el profesor'}`;

    const handleClose = async () => {
        try {
            await whiteboard.closeWhiteboard();
        } catch (err) {
            toast.error(err?.message || 'No se pudo cerrar la pizarra');
        }
    };

    return (
        <div className="vc-wb is-active">
            <div ref={hostRef} className="vc-wb__host" />

            {!supported && (
                <div className="vc-wb__fallback">
                    <p className="vc-wb__fallback-title">
                        Este dispositivo no puede mostrar la pizarra
                    </p>
                    <p>
                        Tu navegador o equipo no soporta la pizarra interactiva de Zoom.
                        Pide al profesor que comparta pantalla para poder seguir la clase.
                    </p>
                </div>
            )}

            {supported && loadFailed && (
                <div className="vc-wb__fallback">
                    <p className="vc-wb__fallback-title">No se pudo cargar la pizarra</p>
                    <p>
                        Zoom no pudo conectar la pizarra con esta sesión. Verifica que la
                        función Whiteboard esté habilitada en la cuenta del Video SDK.
                    </p>
                    {presenting && (
                        <button type="button" className="vc-wb__btn" onClick={handleClose}>
                            Cerrar pizarra
                        </button>
                    )}
                </div>
            )}

            {supported && !loadFailed && attached && !loaded && (
                <div className="vc-wb__fallback">
                    <p className="vc-wb__fallback-title">Cargando la pizarra…</p>
                    <p>Esto puede tardar unos segundos.</p>
                </div>
            )}

            <div className="vc-wb__chrome">
                <span className="vc-wb__label">{label}</span>
                {deviceLimited && supported && (
                    <span className="vc-wb__warn">
                        Tu dispositivo es limitado: la pizarra puede verse lenta o solo como imagen
                    </span>
                )}
                <span className="vc-wb__spacer" />
                {presenting && (
                    <button
                        type="button"
                        className="vc-wb__btn is-danger"
                        onClick={handleClose}
                    >
                        Cerrar pizarra
                    </button>
                )}
            </div>
        </div>
    );
};
